import React from 'react';
import { motion } from 'framer-motion';
import { FaAward, FaGlobeEurope, FaHandshake, FaLightbulb } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const PARTNERS = ['aws.png','azure.png','gcp.png','microsoft365.png','okta.png','vmware.svg','omnissa.png','atlassian.png','zscaler.png'];

const About = ({ market, content }) => {
  const b = getBrand(market);

  const values = [
    {
      icon: <FaHandshake size={36} color={b.accent} />,
      title: 'Partnership Approach',
      description: 'We work with you, not just for you. Your success is our success.'
    },
    {
      icon: <FaLightbulb size={36} color={b.accent} />,
      title: 'Practical Solutions',
      description: 'No unnecessary complexity — we recommend what actually works for your business.'
    },
    {
      icon: <FaAward size={36} color={b.accent} />,
      title: 'Expertise You Can Trust',
      description: 'Over a decade of experience with leading technology platforms and frameworks.'
    },
    {
      icon: <FaGlobeEurope size={36} color={b.accent} />,
      title: market === 'lu' ? 'Local Luxembourg Focus' : 'Global Reach',
      description: market === 'lu'
        ? 'Understanding the unique needs of Luxembourg businesses and the regulatory environment.'
        : 'Serving clients across industries worldwide with expert IT solutions.'
    }
  ];

  const ctaLink     = market === 'bg' ? 'https://outlook.office.com/owa/calendar/MindIT@mindit.bg/bookings/' : '/contact';
  const ctaExternal = market === 'bg';

  /* ── shared section styles ── */
  const secLight = { background: b.bg,    padding: b.secPad };
  const secAlt   = { background: b.bgAlt, padding: b.secPad };

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title={`About ${content.companyName}`}
        subtitle={content.aboutText}
      />

      {/* ── MISSION ── */}
      <section style={secLight}>
        <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
          <span className="eyebrow" style={{ color: b.eyebrowColor }}>Our Mission</span>
          <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, marginBottom: '20px', letterSpacing: '-0.02em' }}>
            {market === 'lu'
              ? 'Making enterprise IT accessible to every business'
              : 'Empowering businesses through smarter technology'}
          </h2>
          <p style={{ fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', lineHeight: 1.8, color: b.textMuted, maxWidth: '700px', margin: '0 auto' }}>
            {market === 'lu'
              ? 'To make enterprise-grade IT solutions accessible and affordable for Luxembourg small and micro businesses, helping them compete in an increasingly digital world.'
              : 'To empower businesses with innovative IT solutions that drive efficiency, security, and growth across on-premise, hybrid, and cloud-native infrastructures.'}
          </p>
        </div>
      </section>

      {/* ── VALUES — 4 items → 2×2 grid ── */}
      <section style={secAlt}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>What Drives Us</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Our Values
            </h2>
          </div>

          {/* 4 cards in a fixed 2×2 grid */}
          <div className="grid-4">
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ padding: '32px 28px', borderRadius: b.cardRadius, boxShadow: b.cardShadow, background: b.bgCard }}
              >
                <div style={{ marginBottom: '18px' }}>{v.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>
                  {v.title}
                </h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.75, color: b.textMuted }}>{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECHNOLOGY PARTNERS ── */}
      {content.showPartnerLogos && (
        <section style={secLight}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span className="eyebrow" style={{ color: b.eyebrowColor }}>
                {market === 'lu' ? 'Trusted Technology Partners' : 'Technology Partners'}
              </span>
              <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '12px' }}>
                Built on industry-leading platforms
              </h2>
              <p style={{ color: b.textMuted, maxWidth: '560px', margin: '0 auto', fontSize: b.fz.base, lineHeight: 1.75 }}>
                We partner with best-in-class technology providers to deliver proven, enterprise-grade solutions.
              </p>
            </div>
            <div className="partner-logo-row">
              {PARTNERS.map((logo, idx) => (
                <img key={idx} src={`/logos/${logo}`} alt={logo.split('.')[0]} className="partner-logo-img" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── WHY WORK WITH US ── */}
      <section style={{
        background: b.isMWO ? '#111318' : '#0F1B45',
        padding: b.secPad
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: b.isMWO ? 'left' : 'center' }}>
          {b.isMWO && <div style={{ width: '40px', height: '3px', background: '#00BCD4', marginBottom: '28px' }} />}
          <h2 style={{
            fontFamily: b.fontDisplay,
            fontSize: b.h2size,
            color: '#ffffff',
            marginBottom: '20px',
            letterSpacing: '-0.02em',
            lineHeight: 1.15
          }}>
            Why work with us?
          </h2>
          <p style={{ fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', lineHeight: 1.8, color: b.isMWO ? 'rgba(232,228,220,0.65)' : 'rgba(255,255,255,0.8)', maxWidth: '700px', margin: b.isMWO ? '0' : '0 auto' }}>
            {market === 'lu'
              ? 'We understand that small businesses need results fast, without breaking the bank. Our solutions are designed to deliver immediate value with clear ROI. You get expert guidance without the enterprise price tag.'
              : 'Our team combines deep technical knowledge with a human-centered approach to deliver real outcomes. From digital transformation to managed operations and cloud migrations — we partner with our clients through every stage of their journey.'}
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Ready to get started?"
        body={market === 'lu' ? 'Book your free IT assessment and discover how we can modernize your business.' : 'Let\'s discuss how we can help drive your business forward with innovative IT solutions.'}
        ctaText={market === 'lu' ? 'Get Free Assessment' : 'Schedule a Consultation'}
        ctaLink={ctaLink}
        ctaExternal={ctaExternal}
      />
    </div>
  );
};

export default About;
